import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './MessagesChat.css';
import { getT, formatT } from '../i18n';
import { parseApiDateTime, formatApiDateTime } from '../utils/messageConversations';
import { buildAttachmentEmbedUrl } from '../utils/attachmentViewer';

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2"/>
    <path d="M20 20L16.5 16.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);


export const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return parts.slice(0, 2).map((part) => part[0]).join('').toUpperCase();
};

export const formatRelativeTime = (dateStr, lang = 'en') => {
  const date = parseApiDateTime(dateStr);
  if (!date) return '';
  const tc = getT(lang).common;
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return tc.justNow;
  if (mins < 60) return formatT(tc.minutesAgo, { n: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return formatT(tc.hoursAgo, { n: hours });
  const days = Math.floor(hours / 24);
  if (days < 7) return formatT(tc.daysAgo, { n: days });
  return formatApiDateTime(dateStr, lang);
};

export const formatBubbleTime = (dateStr, lang = 'en') => formatApiDateTime(dateStr, lang);

const isApplicantSender = (sender) => Boolean(sender && sender.includes(' - Applicant'));

const parseAdminSenderLabel = (sender) => {
  if (!sender || isApplicantSender(sender)) return null;
  const trimmed = String(sender).trim();
  if (!trimmed) return null;
  const atMatch = trimmed.match(/^(.*?)\s+at\s+(.+)$/i);
  if (atMatch) {
    return {
      name: atMatch[1].trim() || 'Admin',
      role: atMatch[2].trim(),
      raw: trimmed,
    };
  }
  return { name: trimmed, role: '', raw: trimmed };
};

const isAdminSourceMessage = (source) => {
  if (!source) return false;
  if (source.notification_type === 'admin_reply') return true;
  if (source.notification_type === 'contact_message') return false;
  if (source.sender && !isApplicantSender(source.sender)) return true;
  return false;
};

/** Last admin who replied in this conversation (for fellow admins to see who is talking). */
const getLastAdminHandler = (msg) => {
  const sources = Array.isArray(msg?._sourceMessages) && msg._sourceMessages.length > 0
    ? [...msg._sourceMessages].reverse()
    : (msg ? [msg] : []);

  for (const source of sources) {
    const replies = Array.isArray(source.replies) ? [...source.replies].reverse() : [];
    for (const reply of replies) {
      const parsed = parseAdminSenderLabel(reply?.sender);
      if (parsed) return parsed;
    }
    if (isAdminSourceMessage(source)) {
      const parsed = parseAdminSenderLabel(source.sender);
      if (parsed) return parsed;
    }
  }
  return null;
};

const findAdminAccountMatch = (handler, adminUsers = []) => {
  if (!handler?.name || !Array.isArray(adminUsers) || adminUsers.length === 0) return null;
  const target = handler.name.trim().toLowerCase();
  return adminUsers.find((admin) => {
    const name = String(admin.name || admin.full_name || '').trim().toLowerCase();
    return name && name === target;
  }) || null;
};

/** Compact label: first name only so fellow admins can see who is chatting. */
const getHandlerChipLabel = (handler) => {
  if (!handler?.name) return '';
  const parts = handler.name.trim().split(/\s+/).filter(Boolean);
  return parts[0] || handler.name.trim();
};

const AdminHandlerAvatar = ({ filename, initials }) => {
  const url = filename ? buildAttachmentEmbedUrl(filename) : null;
  if (url) {
    return <img src={url} alt="" className="chat-conv-handler-avatar-img" />;
  }
  return initials || '?';
};

const getLatestSourceMessage = (msg) => {
  if (Array.isArray(msg._sourceMessages) && msg._sourceMessages.length > 0) {
    return msg._sourceMessages[msg._sourceMessages.length - 1];
  }
  return msg;
};

const getConversationPreview = (msg) => {
  const latest = getLatestSourceMessage(msg);
  const latestReply = latest.replies && latest.replies.length > 0 ? latest.replies[latest.replies.length - 1] : null;
  if (latestReply?.text) return latestReply.text;
  return latest.message || msg.message || '';
};

const getConversationTime = (msg, lang) => {
  const latest = getLatestSourceMessage(msg);
  const latestReply = latest.replies && latest.replies.length > 0 ? latest.replies[latest.replies.length - 1] : null;
  const dateStr = latestReply?.createdAt || latest.createdAt || msg.createdAt;
  return formatApiDateTime(dateStr, lang);
};

const getLastMessageSender = (msg) => {
  const latest = getLatestSourceMessage(msg);
  const latestReply = latest.replies && latest.replies.length > 0 ? latest.replies[latest.replies.length - 1] : null;
  if (latestReply) return isApplicantSender(latestReply.sender) ? 'applicant' : 'admin';
  return !latest.notification_type || latest.notification_type === 'contact_message' ? 'applicant' : 'admin';
};

const isUnreadConversation = (msg, mode) => {
  const sender = getLastMessageSender(msg);
  if (mode === 'admin') return sender === 'applicant';
  if (mode === 'applicant') return sender === 'admin';
  return false;
};

const isRepliedConversation = (msg, mode) => {
  if (mode === 'admin') return getLastMessageSender(msg) === 'admin';
  return false;
};

const appendMessageToThread = (items, msg, applicantName, mode, hiddenMessageIds) => {
  const isApplicantMode = mode === 'applicant';
  const displayApplicantName = msg.name || applicantName || 'Applicant';
  const fromApplicant = !msg.notification_type || msg.notification_type === 'contact_message';
  const notificationId = Number(msg.id);
  const hasNotificationId = Number.isFinite(notificationId) && notificationId > 0;

  if (hasNotificationId && hiddenMessageIds?.has(String(notificationId))) {
    return;
  }

  if (msg.message?.trim()) {
    items.push({
      id: `msg-${msg.id}`,
      notificationId: hasNotificationId ? notificationId : null,
      text: msg.message,
      createdAt: msg.createdAt,
      isOutgoing: isApplicantMode ? fromApplicant : !fromApplicant,
      canManage: hasNotificationId && (isApplicantMode ? fromApplicant : !fromApplicant),
      avatarName: fromApplicant
        ? (isApplicantMode ? (applicantName || 'You') : displayApplicantName)
        : (msg.sender?.replace(/\s+at\s+.+$/, '') || 'Admin'),
      isAdminAvatar: !fromApplicant
    });
  }

  (msg.replies || []).forEach((reply) => {
    const replyFromApplicant = isApplicantSender(reply.sender);
    const replyNotificationId = Number(reply.id);
    const replyHasId = Number.isFinite(replyNotificationId) && replyNotificationId > 0;

    if (replyHasId && hiddenMessageIds?.has(String(replyNotificationId))) {
      return;
    }

    items.push({
      id: replyHasId ? `msg-${reply.id}` : String(reply.id),
      notificationId: replyHasId ? replyNotificationId : null,
      text: reply.text,
      createdAt: reply.createdAt,
      isOutgoing: isApplicantMode ? replyFromApplicant : !replyFromApplicant,
      canManage: replyHasId && (isApplicantMode ? replyFromApplicant : !replyFromApplicant),
      avatarName: replyFromApplicant
        ? (isApplicantMode ? (applicantName || 'You') : displayApplicantName)
        : (reply.sender?.replace(/\s+at\s+.+$/, '') || 'Admin'),
      isAdminAvatar: !replyFromApplicant
    });
  });
};

const buildApplicantThread = (msg, userName, hiddenMessageIds) => {
  const items = [];

  if (Array.isArray(msg._sourceMessages) && msg._sourceMessages.length > 0) {
    msg._sourceMessages.forEach((sourceMessage) => {
      appendMessageToThread(items, sourceMessage, userName, 'applicant', hiddenMessageIds);
    });
    return items;
  }

  appendMessageToThread(items, msg, userName, 'applicant', hiddenMessageIds);
  return items;
};

const buildAdminThread = (msg, hiddenMessageIds) => {
  const applicantName = msg.name || 'Applicant';
  const items = [];

  if (Array.isArray(msg._sourceMessages) && msg._sourceMessages.length > 0) {
    msg._sourceMessages.forEach((sourceMessage) => {
      appendMessageToThread(items, sourceMessage, applicantName, 'admin', hiddenMessageIds);
    });
    return items;
  }

  appendMessageToThread(items, msg, applicantName, 'admin', hiddenMessageIds);
  return items;
};

const DotsVerticalIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <circle cx="12" cy="5" r="1.75" />
    <circle cx="12" cy="12" r="1.75" />
    <circle cx="12" cy="19" r="1.75" />
  </svg>
);

const MESSAGE_MENU_WIDTH = 168;
const MESSAGE_MENU_GAP = 6;

const computeMessageMenuPosition = (buttonEl, isOutgoing) => {
  if (!buttonEl || typeof window === 'undefined') return null;

  const rect = buttonEl.getBoundingClientRect();
  const estimatedHeight = 148;
  const viewportPadding = 8;

  let left = isOutgoing ? rect.right - MESSAGE_MENU_WIDTH : rect.left;
  let top = rect.bottom + MESSAGE_MENU_GAP;

  if (top + estimatedHeight > window.innerHeight - viewportPadding) {
    top = rect.top - estimatedHeight - MESSAGE_MENU_GAP;
  }

  left = Math.max(viewportPadding, Math.min(left, window.innerWidth - MESSAGE_MENU_WIDTH - viewportPadding));
  top = Math.max(viewportPadding, Math.min(top, window.innerHeight - estimatedHeight - viewportPadding));

  return { top, left };
};

const MessagesChat = ({
  lang = 'en',
  title,
  subtitle,
  messages = [],
  selectedMessageId,
  onSelectMessage,
  searchQuery,
  onSearchChange,
  replyInput,
  onReplyChange,
  onSubmitReply,
  mode = 'applicant',
  currentUserName = '',
  adminUsers = [],
  headerActions = null,
  onClose = null,
  beforeBody = null,
  onDeleteMessage = null,
  onEditChatMessage = null,
  onDeleteChatMessage = null,
  hiddenMessageIds = null,
  searchPlaceholder,
  emptyListMessage,
  emptySearchMessage,
  emptySelectionMessage
}) => {
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [filterTab, setFilterTab] = useState('all');
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 1024
  );
  const [mobileShowThread, setMobileShowThread] = useState(false);
  const [openMenuItem, setOpenMenuItem] = useState(null);
  const [deleteSubmenuId, setDeleteSubmenuId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const [menuPosition, setMenuPosition] = useState(null);
  const menuRef = useRef(null);
  const menuButtonRefs = useRef({});
  const tm = getT(lang).messagesChat;
  const tc = getT(lang).common;
  const resolvedSearchPlaceholder = searchPlaceholder || tm.searchPlaceholder;
  const resolvedEmptyList = emptyListMessage || tm.emptyList;
  const resolvedEmptySearch = emptySearchMessage || tm.emptySearch;
  const resolvedEmptySelection = emptySelectionMessage || tm.emptySelect;
  const filteredMessages = messages.filter((msg) => {
    const query = searchQuery.trim().toLowerCase();
    if (query) {
      const preview = getConversationPreview(msg).toLowerCase();
      const matchesSearch = (
        (msg.name || '').toLowerCase().includes(query) ||
        (msg.email || '').toLowerCase().includes(query) ||
        preview.includes(query) ||
        (mode === 'admin' && (getLastAdminHandler(msg)?.raw || '').toLowerCase().includes(query)) ||
        (mode === 'applicant' && 'admin'.includes(query))
      );
      if (!matchesSearch) return false;
    }
    if (filterTab === 'unread') return isUnreadConversation(msg, mode);
    if (filterTab === 'replied') return isRepliedConversation(msg, mode);
    return true;
  });

  const matchMessageId = (msgId, targetId) => String(msgId) === String(targetId);

  const selectedMessage = messages.find((msg) => matchMessageId(msg.id, selectedMessageId));
  const activeMessage = selectedMessage || (mode === 'applicant' ? messages[0] : null);
  const threadItems = useMemo(() => {
    if (!activeMessage) return [];
    return mode === 'applicant'
      ? buildApplicantThread(activeMessage, currentUserName, hiddenMessageIds)
      : buildAdminThread(activeMessage, hiddenMessageIds);
  }, [activeMessage, mode, currentUserName, hiddenMessageIds]);

  const getConvDisplayName = (msg) => (mode === 'applicant' ? tc.admin : (msg.name || tc.applicant));

  const getContactStatus = (msg) => {
    if (mode === 'applicant') return tm.activeNow;
    return msg.email || tc.applicant;
  };

  const composeRef = useRef(null);
  const threadRef = useRef(null);
  const shouldAutoScrollRef = useRef(true);
  const prevThreadCountRef = useRef(0);
  const lastThreadTailRef = useRef('');

  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth <= 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!isNarrow) setMobileShowThread(false);
  }, [isNarrow]);

  useEffect(() => {
    shouldAutoScrollRef.current = true;
    prevThreadCountRef.current = 0;
    lastThreadTailRef.current = '';
  }, [selectedMessageId]);

  useEffect(() => {
    const el = threadRef.current;
    if (!el) return undefined;

    const handleScroll = () => {
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      shouldAutoScrollRef.current = distanceFromBottom < 80;
    };

    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [selectedMessageId]);

  const lastThreadItem = threadItems[threadItems.length - 1];
  const threadTailKey = lastThreadItem
    ? `${lastThreadItem.id}:${lastThreadItem.createdAt}:${lastThreadItem.text || ''}`
    : 'empty';

  useEffect(() => {
    const el = threadRef.current;
    if (!el || threadItems.length === 0) return;

    const prevCount = prevThreadCountRef.current;
    const isConversationSwitch = prevCount === 0;
    const hasNewMessages = threadItems.length > prevCount;
    const tailChanged = threadTailKey !== lastThreadTailRef.current;

    prevThreadCountRef.current = threadItems.length;
    lastThreadTailRef.current = threadTailKey;

    if (!isConversationSwitch && !hasNewMessages && !tailChanged) {
      return;
    }

    if (!isConversationSwitch && (hasNewMessages || tailChanged) && !shouldAutoScrollRef.current) {
      return;
    }

    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  }, [selectedMessageId, threadItems.length, threadTailKey]);

  useEffect(() => {
    const el = composeRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.max(44, Math.min(el.scrollHeight, 120))}px`;
  }, [replyInput]);

  const handleComposeKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (replyInput.trim()) {
        onSubmitReply();
      }
    }
  };

  const canDelete = typeof onDeleteMessage === 'function' && mode === 'admin';
  const canManageMessages = typeof onEditChatMessage === 'function' && typeof onDeleteChatMessage === 'function';

  const closeMessageMenu = () => {
    setOpenMenuItem(null);
    setDeleteSubmenuId(null);
    setMenuPosition(null);
  };

  const setMenuButtonRef = useCallback((itemId, element) => {
    if (element) {
      menuButtonRefs.current[itemId] = element;
      return;
    }
    delete menuButtonRefs.current[itemId];
  }, []);

  const updateMenuPosition = useCallback((item) => {
    if (!item) return;
    const buttonEl = menuButtonRefs.current[item.id];
    const nextPosition = computeMessageMenuPosition(buttonEl, item.isOutgoing);
    if (nextPosition) {
      setMenuPosition(nextPosition);
    }
  }, []);

  const startEditing = (item) => {
    setEditingId(item.id);
    setEditDraft(item.text || '');
    closeMessageMenu();
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditDraft('');
  };

  const saveEditing = async (item) => {
    const trimmed = editDraft.trim();
    if (!trimmed || !item.notificationId) return;
    const saved = await onEditChatMessage(item.notificationId, trimmed);
    if (saved) {
      setEditingId(null);
      setEditDraft('');
    }
  };

  const handleUnsend = async (item, scope) => {
    if (!item.notificationId) return;
    const deleted = await onDeleteChatMessage(item.notificationId, scope);
    if (deleted) {
      closeMessageMenu();
      if (editingId === item.id) cancelEditing();
    }
  };

  const toggleMessageMenu = (item) => {
    setDeleteSubmenuId(null);
    if (openMenuItem?.id === item.id) {
      closeMessageMenu();
      return;
    }
    setOpenMenuItem(item);
    requestAnimationFrame(() => updateMenuPosition(item));
  };

  const toggleDeleteSubmenu = (itemId) => {
    setDeleteSubmenuId((prev) => (prev === itemId ? null : itemId));
  };

  useEffect(() => {
    if (!openMenuItem) return undefined;

    const handlePointerDown = (event) => {
      const target = event.target;
      if (menuRef.current?.contains(target)) return;
      const buttonEl = menuButtonRefs.current[openMenuItem.id];
      if (buttonEl?.contains(target)) return;
      closeMessageMenu();
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [openMenuItem]);

  useEffect(() => {
    if (!openMenuItem) return undefined;

    const handleReposition = () => updateMenuPosition(openMenuItem);
    handleReposition();

    const threadEl = threadRef.current;
    threadEl?.addEventListener('scroll', handleReposition, { passive: true });
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    return () => {
      threadEl?.removeEventListener('scroll', handleReposition);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [openMenuItem, updateMenuPosition]);

  useEffect(() => {
    closeMessageMenu();
    cancelEditing();
  }, [selectedMessageId]);

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
  };

  const toggleConversationSelection = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === filteredMessages.length) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(filteredMessages.map((msg) => msg.id)));
  };

  const handleDeleteSelected = () => {
    if (!canDelete || selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    const confirmed = window.confirm(formatT(tm.deleteConfirm, { n: ids.length }));
    if (!confirmed) return;
    onDeleteMessage(ids);
    if (selectedMessageId && ids.includes(selectedMessageId)) {
      const remaining = filteredMessages.filter((msg) => !ids.includes(msg.id));
      onSelectMessage(remaining[0]?.id ?? null);
    }
    exitSelectMode();
  };

  const handleDeleteCurrent = () => {
    if (!canDelete || !selectedMessage) return;
    const confirmed = window.confirm(formatT(tm.deleteConfirm, { n: 1 }));
    if (!confirmed) return;
    onDeleteMessage(selectedMessage.id);
    const remaining = filteredMessages.filter((msg) => msg.id !== selectedMessage.id);
    onSelectMessage(remaining[0]?.id ?? null);
    setMobileShowThread(false);
  };

  const handleSelectConversation = (id) => {
    if (selectMode) {
      toggleConversationSelection(id);
      return;
    }
    onSelectMessage(id);
    if (isNarrow && mode === 'admin') {
      setMobileShowThread(true);
    }
  };

  const handleMobileBackToInbox = () => {
    setMobileShowThread(false);
  };

  const shellBodyClass = [
    'chat-shell-body',
    isNarrow && mode === 'admin' && mobileShowThread ? 'chat-shell-body--mobile-thread' : '',
    isNarrow && mode === 'admin' && !mobileShowThread ? 'chat-shell-body--mobile-inbox' : ''
  ].filter(Boolean).join(' ');

  useEffect(() => {
    setSelectedIds((prev) => {
      const next = new Set([...prev].filter((id) => messages.some((msg) => msg.id === id)));
      return next.size === prev.size ? prev : next;
    });
  }, [messages]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key !== 'Escape') return;

      if (selectMode) {
        event.stopPropagation();
        exitSelectMode();
        return;
      }

      if (openMenuItem || editingId) {
        event.stopPropagation();
        closeMessageMenu();
        cancelEditing();
        return;
      }

      if (isNarrow && mode === 'admin' && mobileShowThread) {
        event.stopPropagation();
        setMobileShowThread(false);
        return;
      }

      if (typeof onClose === 'function') {
        event.stopPropagation();
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape, true);
    return () => window.removeEventListener('keydown', handleEscape, true);
  }, [selectMode, isNarrow, mode, mobileShowThread, onClose, openMenuItem, editingId]);

  const renderMessageMenu = (item) => {
    if (!openMenuItem || openMenuItem.id !== item.id || !menuPosition) return null;

    return createPortal(
      <div
        ref={menuRef}
        className="chat-bubble-menu chat-bubble-menu--portal"
        role="menu"
        style={{ top: `${menuPosition.top}px`, left: `${menuPosition.left}px` }}
      >
        <button type="button" role="menuitem" onClick={() => startEditing(item)}>
          {tm.editMessage}
        </button>
        <button
          type="button"
          role="menuitem"
          className="chat-bubble-menu-item--danger"
          onClick={() => toggleDeleteSubmenu(item.id)}
          aria-expanded={deleteSubmenuId === item.id}
        >
          {tm.deleteMessage}
        </button>
        {deleteSubmenuId === item.id && (
          <div className="chat-bubble-submenu" role="menu">
            <button type="button" role="menuitem" onClick={() => handleUnsend(item, 'for_me')}>
              {tm.unsendForYou}
            </button>
            <button
              type="button"
              role="menuitem"
              className="chat-bubble-menu-item--danger"
              onClick={() => handleUnsend(item, 'for_everyone')}
            >
              {tm.unsendForEveryone}
            </button>
          </div>
        )}
      </div>,
      document.body
    );
  };

  const renderMessageActions = (item) => {
    if (!canManageMessages || !item.canManage) return null;

    return (
      <div className="chat-bubble-actions">
        <button
          type="button"
          ref={(element) => setMenuButtonRef(item.id, element)}
          className="chat-bubble-menu-btn"
          onClick={() => toggleMessageMenu(item)}
          aria-label={tm.messageActions}
          aria-expanded={openMenuItem?.id === item.id}
        >
          <DotsVerticalIcon />
        </button>
        {renderMessageMenu(item)}
      </div>
    );
  };

  return (
    <div className={`chat-app-shell${mode === 'applicant' ? ' chat-app-shell--applicant' : ' chat-app-shell--formal'}`}>
      {beforeBody}

      <div className={shellBodyClass}>
        <aside className="chat-sidebar">
          {mode === 'admin' && (
              <div className="chat-inbox-header">
              <div className="chat-inbox-header-text admin-section-heading-wrap">
                <h1 className="admin-page-title">{title}</h1>
                <p className="admin-page-desc">{subtitle}</p>
              </div>
              {(headerActions || onClose) && (
                <div className="messages-page-actions">
                  {headerActions}
                  {onClose && (
                    <button type="button" className="chat-shell-close-btn" onClick={onClose} aria-label={tc.close}>
                      &times;
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          <div className="chat-sidebar-toolbar">
            <div className="chat-search-wrap">
              <SearchIcon />
              <input
                type="text"
                className="chat-search-input"
                placeholder={resolvedSearchPlaceholder}
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
              />
            </div>
            {mode === 'admin' && (
              <div className="chat-filter-row">
                <div className="chat-filter-tabs" role="tablist" aria-label={tm.filterAll}>
                  {[
                    { id: 'all', label: tm.filterAll },
                    { id: 'unread', label: tm.filterUnread },
                    { id: 'replied', label: tm.filterReplied }
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={filterTab === tab.id}
                      className={`chat-filter-tab${filterTab === tab.id ? ' active' : ''}`}
                      onClick={() => setFilterTab(tab.id)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                {canDelete && !selectMode && (
                  <button
                    type="button"
                    className="chat-select-btn"
                    onClick={() => setSelectMode(true)}
                  >
                    {tm.selectMode}
                  </button>
                )}
              </div>
            )}
            {canDelete && selectMode && (
              <div className="chat-select-toolbar">
                <button type="button" className="chat-select-action" onClick={handleSelectAll}>
                  {tm.selectAll}
                </button>
                <button
                  type="button"
                  className="chat-select-action chat-select-action--danger"
                  onClick={handleDeleteSelected}
                  disabled={selectedIds.size === 0}
                >
                  {tm.deleteSelected}{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
                </button>
                <button type="button" className="chat-select-action" onClick={exitSelectMode}>
                  {tm.cancelSelect}
                </button>
              </div>
            )}
          </div>
          <div className="chat-conversation-list">
            {filteredMessages.length === 0 ? (
              <div className="chat-sidebar-empty">
                <div className="chat-empty-icon" aria-hidden="true" />
                <p>{messages.length === 0 ? resolvedEmptyList : resolvedEmptySearch}</p>
              </div>
            ) : (
              filteredMessages.map((msg) => {
                const displayName = getConvDisplayName(msg);
                const isAdminConv = mode === 'applicant';
                const isChecked = selectedIds.has(msg.id);
                const isUnread = isUnreadConversation(msg, mode);
                const adminHandler = mode === 'admin' ? getLastAdminHandler(msg) : null;
                const matchedAdmin = adminHandler ? findAdminAccountMatch(adminHandler, adminUsers) : null;
                return (
                  <button
                    key={msg.id}
                    type="button"
                    onClick={() => handleSelectConversation(msg.id)}
                    className={`chat-conv-item ${!selectMode && matchMessageId(selectedMessageId, msg.id) ? 'active' : ''} ${selectMode && isChecked ? 'selected' : ''} ${isUnread ? 'unread' : ''}`}
                  >
                    {selectMode && (
                      <span className={`chat-conv-checkbox ${isChecked ? 'is-checked' : ''}`} aria-hidden="true" />
                    )}
                    <div className={`chat-avatar ${isAdminConv ? 'admin' : ''}`}>
                      {isAdminConv ? 'A' : getInitials(displayName)}
                    </div>
                    <div className="chat-conv-body">
                      <div className="chat-conv-top">
                        <span className="chat-conv-name">{displayName}</span>
                        <span className="chat-conv-time">{getConversationTime(msg, lang)}</span>
                      </div>
                      {mode === 'admin' && (
                        <div className="chat-conv-meta">
                          {msg.email && (
                            <span className="chat-conv-contact" title={msg.email}>{msg.email}</span>
                          )}
                          {adminHandler ? (
                            <span className="chat-conv-handler-chip" title={adminHandler.raw}>
                              <span className="chat-conv-handler-avatar" aria-hidden="true">
                                <AdminHandlerAvatar
                                  filename={matchedAdmin?.avatar}
                                  initials={getInitials(adminHandler.name)}
                                />
                              </span>
                              <span className="chat-conv-handler-label">{getHandlerChipLabel(adminHandler)}</span>
                            </span>
                          ) : (
                            <span className="chat-conv-handler-chip is-waiting">{tm.awaitingAdminReply}</span>
                          )}
                        </div>
                      )}
                      <p className="chat-conv-preview">{getConversationPreview(msg)}</p>
                    </div>
                    {isUnread && !selectMode && (
                      <span className="chat-conv-unread-dot" aria-label={tm.filterUnread} />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </aside>

        <main className="chat-main">
          {activeMessage ? (
            <>
              <div className="chat-main-header">
                {isNarrow && mode === 'admin' && (
                  <button
                    type="button"
                    className="chat-mobile-back-btn"
                    onClick={handleMobileBackToInbox}
                    aria-label={tm.backToConversations}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                )}
                <div className="chat-main-header-info">
                  <div className={`chat-avatar ${mode === 'applicant' ? 'admin' : ''}`}>
                    {mode === 'applicant' ? 'A' : getInitials(activeMessage.name)}
                  </div>
                  <div className="chat-main-header-text">
                    <p className="chat-contact-name">{getConvDisplayName(activeMessage)}</p>
                    <p className="chat-contact-status">
                      {mode === 'applicant' && <span className="chat-status-dot" aria-hidden="true" />}
                      {getContactStatus(activeMessage)}
                    </p>
                  </div>
                </div>
                {canDelete && !selectMode && (
                  <button
                    type="button"
                    className="chat-delete-btn"
                    onClick={handleDeleteCurrent}
                    aria-label={tm.deleteConversation}
                    title={tm.deleteConversation}
                  />
                )}
              </div>

              <div className="chat-thread" ref={threadRef}>
                {threadItems.length === 0 ? (
                  <div className="chat-thread-empty">
                    <div className="chat-empty-icon chat-empty-icon--main" aria-hidden="true" />
                    <p>{tm.emptyThread}</p>
                  </div>
                ) : (
                  threadItems
                    .filter((item) => (item.text || '').trim())
                    .map((item) => (
                    <div
                      key={item.id}
                      className={`chat-bubble-row ${item.isOutgoing ? 'outgoing' : 'incoming'}${mode === 'applicant' ? ' chat-bubble-row--applicant' : ''}`}
                    >
                      <div className="chat-bubble-row-inner">
                        {item.isOutgoing && renderMessageActions(item)}
                        <div className="chat-bubble">
                          {editingId === item.id ? (
                            <>
                              <textarea
                                className="chat-bubble-edit-input"
                                value={editDraft}
                                onChange={(e) => setEditDraft(e.target.value)}
                                rows={3}
                                aria-label={tm.editMessage}
                              />
                              <div className="chat-bubble-edit-actions">
                                <button
                                  type="button"
                                  className="chat-bubble-edit-save"
                                  onClick={() => saveEditing(item)}
                                  aria-label={tm.saveEdit}
                                  title={tm.saveEdit}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                    <path d="M5 12.5l4.5 4.5L19 7.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                </button>
                                <button
                                  type="button"
                                  className="chat-bubble-edit-cancel"
                                  onClick={cancelEditing}
                                  aria-label={tm.cancelEdit}
                                  title={tm.cancelEdit}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                    <path d="M7 7l10 10M17 7L7 17" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                                  </svg>
                                </button>
                              </div>
                            </>
                          ) : (
                            <>
                              <p className="chat-bubble-text">{item.text}</p>
                              <span className="chat-bubble-time">
                                {formatBubbleTime(item.createdAt, lang)}
                              </span>
                            </>
                          )}
                        </div>
                        {!item.isOutgoing && renderMessageActions(item)}
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="chat-compose">
                <div className="chat-compose-inner">
                  <textarea
                    ref={composeRef}
                    className="chat-compose-input"
                    value={replyInput}
                    onChange={(e) => onReplyChange(e.target.value)}
                    onKeyDown={handleComposeKeyDown}
                    placeholder={tm.writeMessage}
                    rows={1}
                    aria-label={tm.writeMessage}
                  />
                  <button
                    type="button"
                    className="chat-send-btn"
                    onClick={onSubmitReply}
                    disabled={!replyInput.trim()}
                    aria-label={tm.sendMessage}
                  >
                    {tm.sendLabel}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="chat-main-empty">
              <div className="chat-empty-icon chat-empty-icon--main" aria-hidden="true" />
              <p>{messages.length === 0 ? resolvedEmptyList : resolvedEmptySelection}</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default MessagesChat;
